import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { enqueueFolder, getPendingFolders, getPendingBons } from "@/lib/offline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Folder, FolderOpen, Pencil, Trash2, Inbox, CloudOff } from "lucide-react";

const NameDialog = ({ open, title, initial, onClose, onSubmit }) => {
  const [name, setName] = useState(initial || "");
  useEffect(() => { setName(initial || ""); }, [initial, open]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="folder-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSubmit(name.trim()); }}>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du dossier"
            className="h-11" data-testid="folder-name-input" />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} data-testid="folder-cancel-btn">Annuler</Button>
            <Button type="submit" disabled={!name.trim()} data-testid="folder-save-btn"
              className="bg-amber-500 font-semibold text-slate-900 hover:bg-amber-400">Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FolderCard = ({ icon: Icon, name, count, archivedCount = 0, pending = 0, pendingFolder, onOpen, actions, testId }) => (
  <div data-testid={testId} onClick={onOpen}
    className="group cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900">
    <div className="flex items-start justify-between">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
        <Icon className="h-5 w-5" />
      </div>
      {actions && <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>{actions}</div>}
    </div>
    <p className="mt-3 line-clamp-2 font-head text-base font-bold text-slate-900 dark:text-white">{name}</p>
    <p className="mt-1 text-xs text-slate-500">
      {count} bon{count > 1 ? "s" : ""}{archivedCount > 0 && ` · ${archivedCount} archivé${archivedCount > 1 ? "s" : ""}`}
    </p>
    {(pending > 0 || pendingFolder) && (
      <p className="mt-2 inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-950/60 dark:text-amber-200" data-testid={`${testId}-pending`}>
        <CloudOff className="h-3 w-3" /> {pendingFolder ? "Dossier en attente" : `${pending} en attente`}
      </p>
    )}
  </div>
);

export const FolderGrid = ({ isAdmin, onOpen, newSignal = 0, onNewHandled }) => {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    if (newSignal > 0) { setDialog({ folder: null }); if (onNewHandled) onNewHandled(); }
  }, [newSignal, onNewHandled]);

  const load = () => api.get("/folders").then((r) => setData(r.data)).catch(() => setData({ folders: [], unfiled: 0 }));
  useEffect(() => {
    load();
    window.addEventListener("ir-synced", load);
    return () => window.removeEventListener("ir-synced", load);
  }, []);

  const save = async (name) => {
    if (!dialog.folder && !navigator.onLine) {
      const exists = [...data.folders, ...getPendingFolders()].some((f) => f.name.toLowerCase() === name.toLowerCase());
      if (exists) { toast.error("Ce dossier existe déjà"); return; }
      enqueueFolder(name);
      toast.success("Hors ligne : dossier créé sur l'appareil, il sera synchronisé au retour du réseau");
      setDialog(null); setData((d) => ({ ...d }));
      return;
    }
    if (!navigator.onLine) { toast.error("Renommer un dossier nécessite le réseau"); return; }
    try {
      if (dialog.folder) await api.put(`/folders/${dialog.folder.id}`, { name });
      else await api.post("/folders", { name });
      toast.success(dialog.folder ? "Dossier renommé" : "Dossier créé");
      setDialog(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Supprimer le dossier « ${f.name} » ?`)) return;
    if (!navigator.onLine) { toast.error("Supprimer un dossier nécessite le réseau"); return; }
    try { await api.delete(`/folders/${f.id}`); toast.success("Dossier supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="py-16 text-center text-sm text-slate-400">Chargement...</div>;

  const pendingFolders = getPendingFolders().filter((p) => !data.folders.some((f) => f.name === p.name));
  const pendingCount = (fid) => getPendingBons(fid).length;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {data.folders.map((f) => (
          <FolderCard key={f.id} testId={`folder-card-${f.id}`} icon={Folder} name={f.name} count={f.count}
            archivedCount={isAdmin ? f.total - f.count : 0} pending={pendingCount(f.id)}
            onOpen={() => onOpen(f.id)}
            actions={isAdmin && (
              <>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDialog({ folder: f })} data-testid={`folder-rename-${f.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                {f.total === 0 && (
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => remove(f)} data-testid={`folder-delete-${f.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </>
            )} />
        ))}
        {pendingFolders.map((f) => (
          <FolderCard key={f.id} testId={`folder-card-${f.id}`} icon={Folder} name={f.name} count={0}
            pending={pendingCount(f.id)} pendingFolder onOpen={() => onOpen(f.id)} />
        ))}
        <FolderCard testId="folder-card-none" icon={data.unfiled ? FolderOpen : Inbox} name="Sans dossier" count={data.unfiled}
          pending={pendingCount("none")} onOpen={() => onOpen("none")} />
      </div>
      <NameDialog open={!!dialog} title={dialog?.folder ? "Renommer le dossier" : "Nouveau dossier"}
        initial={dialog?.folder?.name} onClose={() => setDialog(null)} onSubmit={save} />
    </div>
  );
};
